import React, { useEffect, useState } from "react";
import { getOrderflowStatus } from "../services/api";
import "../styles/OrderFlowGateStatus.css";

const OrderFlowGateStatus = ({ symbol }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!symbol) {
      setLoading(false);
      setStatus(null);
      return;
    }
    let cancelled = false;
    let first = true;
    const load = async () => {
      if (first) setLoading(true);
      try {
        const res = await getOrderflowStatus(symbol);
        if (!cancelled) setStatus(res);
      } catch (e) {
        if (!cancelled) setStatus({ ok: false, error: e.message });
      } finally {
        if (first) {
          first = false;
          if (!cancelled) setLoading(false);
        }
      }
    };
    load();
    // El poller actualiza cada ~minuto; refrescar evita que un "sin datos"
    // de un instante quede congelado en pantalla.
    const id = setInterval(load, 20000);
    return () => { cancelled = true; clearInterval(id); };
  }, [symbol]);

  if (loading) {
    return (
      <div className="orderflow-gate-status">
        <span className="orderflow-gate-badge tone-neutral">Order flow: cargando…</span>
      </div>
    );
  }

  if (!status || !status.ok) {
    return (
      <div className="orderflow-gate-status">
        <span className="orderflow-gate-badge tone-danger">Order flow: sin datos</span>
      </div>
    );
  }

  const data = status.data || {};
  const ob = data.order_book || {};
  const trades = data.market_trades || {};
  const gateEnabled = status.gate_enabled !== false;

  const toMs = (value) => {
    if (typeof value === "string" && value.includes("-")) {
      const parsed = Date.parse(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    const num = Number(value);
    return Number.isFinite(num) && num > 0 ? num : null;
  };
  const fmtTs = (ms) => (ms ? new Date(ms).toLocaleString("es-ES") : "—");

  const obTs = toMs(ob.last_ts);
  const trTs = toMs(trades.last_ts);

  const ageOf = (src, ts) => {
    const serverAge = Number(src?.age_s);
    if (Number.isFinite(serverAge)) return serverAge;
    return ts !== null ? Math.max(0, (Date.now() - ts) / 1000) : null;
  };
  const obAge = ageOf(ob, obTs);
  const trAge = ageOf(trades, trTs);
  const maxAge = [obAge, trAge].filter((v) => v !== null).reduce((a, b) => Math.max(a, b), 0);

  const freshOf = (src, age) => {
    if (typeof src?.fresh === "boolean") return src.fresh;
    const limit = Number(src?.limit_s) || 300;
    return age !== null && age <= limit;
  };
  const obFresh = freshOf(ob, obAge);
  const trFresh = freshOf(trades, trAge);

  const obHas = ob.has_data === true;
  const tradesHas = trades.has_data === true;
  const hasData = obHas && tradesHas && obFresh && trFresh;
  const staleData = (obHas || tradesHas) && !hasData;

  const fmtAge = (seg) => {
    if (seg === null) return "—";
    if (seg < 90) return `hace ${Math.round(seg)} s`;
    if (seg < 5400) return `hace ${Math.round(seg / 60)} min`;
    return `hace ${(seg / 3600).toFixed(1)} h`;
  };

  const prof = data.profundidad || null;
  const flags = status.flags || {};
  const flagOn = (key) => flags[key] !== false;

  const num = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };
  const fmtPrice = (value) => {
    const n = num(value);
    if (n === null || n <= 0) return "—";
    const decimals = n >= 100 ? 2 : n >= 1 ? 4 : 6;
    return n.toLocaleString("es-ES", {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    });
  };
  const fmtPct = (value) => {
    const n = num(value);
    if (n === null) return "—";
    return `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
  };

  const barrido = prof?.barrido_liquidez || null;
  const flujo = prof?.flujo_ordenes || null;
  const vwap = prof?.vwap_anclado || null;
  const perfil = prof?.perfil_volumen || null;
  const senal = prof?.senal_entrada || "NEUTRAL";
  const confianza = num(prof?.confianza) || 0;
  const bloquear = prof?.bloquear_compra === true;

  const toneBarrido = !flagOn("barrido_liquidez")
    ? "tone-neutral"
    : barrido?.detectado
      ? (barrido.direccion === "SELL" ? "tone-danger" : "tone-ok")
      : "tone-neutral";
  const toneFlujo = !flagOn("flujo_ordenes")
    ? "tone-neutral"
    : flujo?.direccion === "ALCISTA"
      ? "tone-ok"
      : flujo?.direccion === "BAJISTA"
        ? "tone-danger"
        : "tone-neutral";
  const extensionMax = 2.0;
  const toneVwap = !flagOn("vwap_anclado")
    ? "tone-neutral"
    : num(vwap?.precio_vs_vwap_pct) !== null && Math.abs(vwap.precio_vs_vwap_pct) > extensionMax
      ? "tone-warning"
      : "tone-neutral";
  const tonePerfil = !flagOn("perfil_volumen")
    ? "tone-neutral"
    : perfil?.esta_en_valor_area
      ? "tone-ok"
      : "tone-warning";
  const toneSenal = bloquear
    ? "tone-danger"
    : senal === "ALCISTA"
      ? "tone-ok"
      : senal === "BAJISTA"
        ? "tone-danger"
        : "tone-neutral";

  const MOTIVO_LABELS = {
    barrido_liquidez_bajista: "barrido de liquidez bajista",
    flujo_ordenes_bajista: "flujo de órdenes bajista",
    precio_extendido_sobre_vwap_anclado: "precio extendido sobre VWAP",
  };
  const fmtMotivo = (raw) =>
    String(raw || "")
      .split(";")
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => MOTIVO_LABELS[part] || part.replace(/_/g, " "))
      .join(" · ");

  return (
    <div className="orderflow-gate-status">
      <div className="orderflow-gate-row">
        <span className={`orderflow-gate-badge ${gateEnabled ? "tone-ok" : "tone-danger"}`}>
          {gateEnabled ? "✅ Order flow: activo" : "⛔ Order flow: desactivado"}
        </span>
        <span className={`orderflow-gate-badge ${hasData ? "tone-ok" : "tone-warning"}`}>
          {hasData
            ? "📊 Datos de profundidad: sí"
            : staleData
              ? `🕒 Datos de profundidad: viejos (${fmtAge(maxAge)})`
              : "⚠️ Datos de profundidad: no"}
        </span>
      </div>

      {prof ? (
        <>
          <div className="orderflow-gate-row">
            <span className="orderflow-gate-label">Indicadores</span>
            <span
              className={`orderflow-gate-badge ${toneFlujo}`}
              title={
                flujo
                  ? `Comprador ${fmtPrice(flujo.volumen_comprador)} · Vendedor ${fmtPrice(flujo.volumen_vendedor)} · ${flujo.num_trades ?? 0} trades`
                  : ""
              }
            >
              {!flagOn("flujo_ordenes")
                ? "🧭 Flujo: off"
                : flujo
                  ? `🧭 Flujo ${flujo.direccion} ${fmtPct(flujo.diferencial_pct)}`
                  : "🧭 Flujo: sin datos"}
            </span>
            <span
              className={`orderflow-gate-badge ${toneBarrido}`}
              title={barrido ? `Motivo: ${barrido.motivo}` : ""}
            >
              {!flagOn("barrido_liquidez")
                ? "🧹 Barrido: off"
                : barrido?.detectado
                  ? `🧹 Barrido ${barrido.direccion} @ ${fmtPrice(barrido.nivel)} · conf ${Math.round((num(barrido.confianza) || 0) * 100)}%`
                  : "🧹 Barrido: no detectado"}
            </span>
            <span
              className={`orderflow-gate-badge ${toneVwap}`}
              title={vwap ? `Volumen acumulado ${fmtPrice(vwap.volumen_acumulado)} · ${vwap.num_trades ?? 0} trades` : ""}
            >
              {!flagOn("vwap_anclado")
                ? "📐 VWAP anclado: off"
                : vwap && num(vwap.vwap_anclado) > 0
                  ? `📐 VWAP anc ${fmtPrice(vwap.vwap_anclado)} (${fmtPct(vwap.precio_vs_vwap_pct)})`
                  : "📐 VWAP anclado: sin datos"}
            </span>
            <span
              className={`orderflow-gate-badge ${tonePerfil}`}
              title={perfil ? `Precio vs POC ${fmtPct(perfil.precio_vs_poc_pct)} · ${perfil.num_bines ?? 0} niveles` : ""}
            >
              {!flagOn("perfil_volumen")
                ? "🧱 Perfil volumen: off"
                : perfil && num(perfil.poc) > 0
                  ? `🧱 POC ${fmtPrice(perfil.poc)} · VA ${fmtPrice(perfil.valor_area_baja)}–${fmtPrice(perfil.valor_area_alta)} ${perfil.esta_en_valor_area ? "✓" : "✗"}`
                  : "🧱 Perfil volumen: sin datos"}
            </span>
          </div>

          <div className="orderflow-gate-row orderflow-gate-verdict">
            <span className="orderflow-gate-label">Veredicto</span>
            <span
              className={`orderflow-gate-badge ${toneSenal}`}
              title={prof.motivo_bloqueo || ""}
            >
              {bloquear
                ? `⛔ Bloquea compra: ${fmtMotivo(prof.motivo_bloqueo) || "sin motivo"}`
                : `🎯 Señal ${senal} · conf ${Math.round(confianza * 100)}%`}
            </span>
          </div>
        </>
      ) : null}

      {(obHas || tradesHas) && (
        <div className="orderflow-gate-row orderflow-gate-source">
          <span className="orderflow-gate-label">Fuente</span>
          {obHas && (
            <span className="orderflow-gate-badge tone-neutral" title={fmtTs(obTs)}>
              Order book: {ob.docs} docs · {fmtAge(obAge)}
            </span>
          )}
          {tradesHas && (
            <span className="orderflow-gate-badge tone-neutral" title={fmtTs(trTs)}>
              Trades: {trades.docs} docs · {fmtAge(trAge)}
            </span>
          )}
        </div>
      )}
      {!obHas && !tradesHas && (
        <div className="orderflow-gate-row">
          <span className="orderflow-gate-badge tone-danger">
            ⚠️ Sin datos en Mongo: el gate no puede evaluar
          </span>
        </div>
      )}
    </div>
  );
};

export default OrderFlowGateStatus;
